/**
 * ACTIVITY-CORE (L3, variante POS) — página "Actividad": quién hizo qué en el
 * local. Fuente local-first: bucket `activity_recent` (~90 días); el historial
 * más antiguo se pagina bajo demanda desde la API (log maestro INMUTABLE).
 *
 * Vista por TURNO (registro por caja): el feed es filtrable por sesión de
 * caja y muestra el resumen antifraude del turno (anulaciones, descuentos,
 * reaperturas) pensado para el cuadre.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, Show } from "solid-js";
import { History, Percent, RotateCcw, XCircle } from "lucide-solid";
import {
  type ActivityRow,
  getActivityFeedQuery,
  getSessionsQuery,
  queryKeys,
  summarizeShiftActivity,
} from "@comandero/shared";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import type { RegisterSession } from "@comandero/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { api } from "@/lib/api";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { ActivityList } from "./activity-list";

const LOCAL_FEED_LIMIT = 200;
const API_PAGE_SIZE = 50;
const ALL_SESSIONS = "__all__";

/** Fila de la API (camelCase) → forma local snake_case del feed. */
function mapApiActivityRow(item: {
  id: string;
  scopeId: string;
  branchId?: string | null;
  registerSessionId?: string | null;
  entityType: string;
  entityId: string;
  action: string;
  actorUserId: string;
  actorName?: string | null;
  actorDeviceId?: string | null;
  platform?: string | null;
  serverTs: string;
  clientTs?: string | null;
  patch: unknown;
  batchId?: string | null;
  revertsActivityId?: string | null;
}): ActivityRow {
  return {
    id: item.id,
    scope_id: item.scopeId,
    branch_id: item.branchId ?? null,
    register_session_id: item.registerSessionId ?? null,
    entity_type: item.entityType,
    entity_id: item.entityId,
    action: item.action,
    actor_user_id: item.actorUserId,
    actor_name: item.actorName ?? null,
    actor_device_id: item.actorDeviceId ?? null,
    platform: item.platform ?? null,
    server_ts: item.serverTs,
    client_ts: item.clientTs ?? null,
    patch: typeof item.patch === "string" ? item.patch : JSON.stringify(item.patch ?? {}),
    batch_id: item.batchId ?? null,
    reverts_activity_id: item.revertsActivityId ?? null,
  };
}

export function ActivityFeedPage(props: { initialSessionId?: string }) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const ctx = useActiveContext();

  // Filtro de turno (sesión de caja). Se inicializa UNA VEZ desde la URL
  // (?session=…, enlace del cierre de caja); después es estado local puro —
  // sin efecto URL→estado que pueda revertir la selección (patrón CC-16).
  const [sessionFilter, setSessionFilter] = createSignal<string>(
    props.initialSessionId ?? ALL_SESSIONS,
  );

  // Historial profundo (API), acumulado bajo el feed local.
  const [olderRows, setOlderRows] = createSignal<ActivityRow[]>([]);
  const [loadingOlder, setLoadingOlder] = createSignal(false);
  const [hasMoreOlder, setHasMoreOlder] = createSignal(true);

  const activeSessionId = () => (sessionFilter() === ALL_SESSIONS ? undefined : sessionFilter());

  const localQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.feed(userId(), { registerSessionId: activeSessionId() }),
    db,
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getActivityFeedQuery(dbInstance, {
            limit: LOCAL_FEED_LIMIT,
            registerSessionId: activeSessionId(),
          })
        : null;
    },
    enabled: () => Boolean(drizzle()),
  });

  // Sesiones de caja de la sucursal activa (para el selector de turno).
  const sessionsQuery = createPowerSyncQuery<RegisterSession>({
    queryKey: () => queryKeys.comandero.sessions(ctx.branch()?.id ?? "none"),
    db,
    queryFn: () => {
      const d = drizzle();
      const org = ctx.org();
      const branch = ctx.branch();
      return d && org && branch ? getSessionsQuery(d, org.id, branch.id) : null;
    },
    enabled: () => Boolean(drizzle() && ctx.org() && ctx.branch()),
  });

  const sessionOptions = createMemo(() => {
    const opts = [{ value: ALL_SESSIONS, label: t(msg`All activity`) }];
    for (const s of sessionsQuery.data ?? []) {
      const opened = s.opened_at ? new Date(s.opened_at).toLocaleString(locale()) : s.id;
      const status = s.status === "open" ? t(msg`open`) : t(msg`closed`);
      opts.push({ value: s.id, label: `${opened} — ${status}` });
    }
    return opts;
  });

  const rows = createMemo<ActivityRow[]>(() => {
    const local = localQuery.data ?? [];
    if (olderRows().length === 0) {
      return local;
    }
    const sid = activeSessionId();
    const older = sid ? olderRows().filter((r) => r.register_session_id === sid) : olderRows();
    const seen = new Set(local.map((r) => r.id));
    return [...local, ...older.filter((r) => !seen.has(r.id))];
  });

  // Resumen antifraude del turno seleccionado (para el cuadre).
  const shiftSummary = createMemo(() =>
    activeSessionId() ? summarizeShiftActivity(rows()) : null,
  );

  const loadOlder = async () => {
    if (loadingOlder()) {
      return;
    }
    setLoadingOlder(true);
    try {
      const last = rows()[rows().length - 1];
      const { data, error } = await api.GET("/api/activity", {
        params: {
          query: {
            before: last?.id ?? null,
            limit: API_PAGE_SIZE,
            registerSessionId: activeSessionId() ?? null,
          },
        },
      });
      if (error || !data) {
        showToast(t(msg`Could not load older activity`), "error");
        return;
      }
      const mapped = data.items.map(mapApiActivityRow);
      setOlderRows((prev) => [...prev, ...mapped]);
      setHasMoreOlder(Boolean(data.nextBefore));
    } catch {
      showToast(t(msg`Could not load older activity`), "error");
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div class="space-y-6" data-testid="activity-page">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <History class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-2xl font-bold tracking-tight">{t(msg`Activity`)}</h1>
            <p class="text-sm text-muted-foreground">
              {t(msg`Who did what in your restaurant — every void, discount and reopen.`)}
            </p>
          </div>
        </div>
        <div class="min-w-52">
          <Select
            options={sessionOptions()}
            value={sessionFilter()}
            onChange={(value) => {
              setSessionFilter(value ?? ALL_SESSIONS);
              setOlderRows([]);
              setHasMoreOlder(true);
            }}
            aria-label={t(msg`Filter by register session`)}
            data-testid="activity-session-filter"
            fluid
          />
        </div>
      </div>

      <Show when={shiftSummary()}>
        {(summary) => (
          <div
            class="grid grid-cols-3 gap-3 rounded-xl border border-border/30 bg-card/60 p-4"
            data-testid="shift-summary"
          >
            <div class="flex items-center gap-2.5">
              <XCircle class="h-4 w-4 text-destructive" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-voids">
                  {summary().voids}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Voids`)}</p>
              </div>
            </div>
            <div class="flex items-center gap-2.5">
              <Percent class="h-4 w-4 text-warning" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-discounts">
                  {summary().discounts}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Discounts`)}</p>
              </div>
            </div>
            <div class="flex items-center gap-2.5">
              <RotateCcw class="h-4 w-4 text-primary" />
              <div>
                <p class="text-lg font-bold leading-none" data-testid="shift-reopens">
                  {summary().reopens}
                </p>
                <p class="mt-1 text-xs text-muted-foreground">{t(msg`Reopens`)}</p>
              </div>
            </div>
          </div>
        )}
      </Show>

      <ActivityList
        rows={rows}
        isLoading={() => localQuery.isFetching && (localQuery.data ?? []).length === 0}
        grouped
        currentUserId={userId}
        canRevert
      />

      <Show when={rows().length >= API_PAGE_SIZE && hasMoreOlder()}>
        <div class="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            disabled={loadingOlder()}
            onClick={() => void loadOlder()}
            data-testid="activity-load-older"
          >
            {loadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
          </Button>
        </div>
      </Show>
    </div>
  );
}
